import { action, computed, observable } from 'mobx'
import type { AlertFactory } from '@/features/shell'
import { type Deactivatable } from '@/infra/activation'
import { type FilterDescription } from '@/infra/FiltersViewModel'
import { extractMessageFromError } from '@/utils/error-util'
import { type JobSearchViewModel } from '@/features/jobs/list/JobSearchViewModel'
import { SavedJobFilterEditorViewModel } from '@/features/jobs/list/SavedJobFilterEditorViewModel'
import {
  type DefaultSavedJobFilterDescription,
  describeDefaultSavedJobFilter,
  maxOwnSavedJobFilters,
  type SavedJobFilter,
  sharedSavedFilterId,
} from '@/features/jobs/list/saved-job-filters'
import {
  decodeSharedJobFilter,
  encodeSharedJobFilter,
  type SharedJobFilter,
  sharedJobFilterParam,
} from '@/features/jobs/list/shared-job-filter'

/**
 * The tabs above the jobs list: their menu, renaming, editing, managing and
 * sharing. Default tabs are fixed; the viewer duplicates one to build on it.
 */
export class JobListTabsViewModel implements Deactivatable {
  /**
   * The tab whose menu is open.
   */
  @observable accessor menuTabId: string | null = null

  /**
   * The tab whose name is being edited in place.
   */
  @observable accessor renamingTabId: string | null = null

  /**
   * The tab waiting for its deletion to be confirmed.
   */
  @observable accessor deletingTabId: string | null = null

  /**
   * Whether putting the defaults back is waiting for confirmation.
   */
  @observable accessor isConfirmingReset = false

  /**
   * The open tab editor.
   */
  @observable.ref accessor editor: SavedJobFilterEditorViewModel | null = null

  /**
   * The tab the editor was opened to create, which goes away unless saved,
   * and the tab to go back to then.
   */
  private _draft: {
    id: string
    returnToId: string | null
    clearsFilterBar: boolean
  } | null = null

  /**
   * @param _selectTab Selects a tab and remembers it.
   * @param _selectRememberedTab Selects the remembered tab without
   * remembering anything new.
   * @param _getShareUrl The jobs page's address with the given query.
   */
  constructor(
    private readonly _searcher: JobSearchViewModel,
    private readonly _selectTab: (id: string) => void,
    private readonly _selectRememberedTab: () => void,
    private readonly _getSharerName: () => string,
    private readonly _getShareUrl: (query: string) => string,
    private readonly _writeToClipboard: (text: string) => Promise<void>,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * The tabs shown, in the viewer's order.
   */
  get tabs(): SavedJobFilter[] {
    return this._searcher.savedFilters
  }

  /**
   * Every tab, shown ones first in order, then hidden ones.
   */
  @computed
  get allTabs(): SavedJobFilter[] {
    const all = this._searcher.savedFilterList.all
    return [...all.filter((t) => !t.isHidden), ...all.filter((t) => t.isHidden)]
  }

  /**
   * The tab opened from a shared link, until it's added or dismissed.
   */
  get sharedTab(): SharedJobFilter | null {
    return this._searcher.sharedFilter
  }

  /**
   * The selected tab's ID.
   */
  get selectedTabId() {
    return this._searcher.savedFilter?.id ?? null
  }

  /**
   * Whether there's room for another tab.
   */
  get canAddTab() {
    return this._searcher.savedFilterList.canAdd
  }

  /**
   * Why a tab can't be added, or `null` when it can.
   */
  @computed
  get addTabHint() {
    return this.canAddTab
      ? null
      : `You can have ${maxOwnSavedJobFilters} tabs of your own. Delete one to add another.`
  }

  /**
   * How many jobs the edited tab shows, or `null` when the filter bar's
   * filters narrow the list further and the count would mislead.
   */
  @computed
  get editorJobCount() {
    return this._searcher.filters.appliedFilterCount > 0
      ? null
      : this._searcher.totalJobs
  }

  /**
   * Defaults the viewer deleted, which they can add back.
   */
  get deletedDefaults() {
    return this._searcher.savedFilterList.deletedDefaults
  }

  /**
   * Whether a tab can be hidden or deleted, which the last one shown can't.
   */
  canRemove(id: string) {
    return this.tabs.length > 1 || !this.tabs.some((t) => t.id === id)
  }

  /** @inheritDoc */
  deactivate() {
    this.closeEditor()
  }

  /**
   * A default tab's description, or `null` for the viewer's own tabs.
   */
  describeDefault(id: string): DefaultSavedJobFilterDescription | null {
    return describeDefaultSavedJobFilter(this._searcher.listPerspective, id)
  }

  /**
   * The name of the default a tab starts from, or `null`.
   */
  baseNameOf(tab: SavedJobFilter): string | null {
    return this._searcher.savedFilterList.defaultOf(tab.baseId)?.name ?? null
  }

  /**
   * A tab's own filters as titles and values, without its default's.
   */
  describe(tab: SavedJobFilter): FilterDescription[] {
    return this._searcher.filters.describe(tab.filters)
  }

  /**
   * Shows a tab's jobs, closing the editor of another.
   */
  @action.bound
  select(id: string) {
    if (this.editor && this.editor.savedFilterId !== id) {
      this.closeEditor()
    }
    this._selectTab(id)
  }

  /**
   * Opens a tab's menu. The shared tab has none.
   */
  @action.bound
  openMenu(id: string) {
    if (id !== sharedSavedFilterId) {
      this.menuTabId = id
    }
  }

  @action.bound
  closeMenu() {
    this.menuTabId = null
  }

  /**
   * Starts renaming one of the viewer's own tabs in place.
   */
  @action.bound
  startRenaming(id: string) {
    this.closeMenu()
    if (this._find(id)?.isDefault === false) {
      this.renamingTabId = id
    }
  }

  /**
   * Renames the tab being renamed. A blank name keeps the old one.
   */
  @action.bound
  rename(name: string) {
    const id = this.renamingTabId
    this.renamingTabId = null
    if (id && name.trim()) {
      this._searcher.savedFilterList.update(id, { name: name.trim() })
    }
  }

  @action.bound
  cancelRenaming() {
    this.renamingTabId = null
  }

  /**
   * Selects one of the viewer's own tabs and opens its editor, which
   * previews its filters in the list while they change.
   */
  @action.bound
  edit(id: string) {
    const tab = this._find(id)
    this.closeMenu()
    if (!tab || tab.isDefault) {
      return
    }
    if (this.editor?.savedFilterId !== id) {
      this.closeEditor()
    }
    this._selectTab(id)
    const list = this._searcher.savedFilterList
    const base = list.defaultOf(tab.baseId)
    const editor = new SavedJobFilterEditorViewModel(
      id,
      tab.name,
      tab.filters,
      base && { name: base.name, keys: Object.keys(base.filters) },
      (filters) => this._searcher.filters.fork(filters),
      (changes) => {
        list.update(id, changes)
        if (this._draft?.clearsFilterBar) {
          this._searcher.filters.clearExplicitFilters()
        }
        this._draft = null
        this.closeEditor()
      },
    )
    editor.activate()
    this.editor = editor
    const baseValues = base ? this._searcher.filters.valuesOf(base.filters) : {}
    this._searcher.setSavedFilterPreview(() => ({
      ...baseValues,
      ...editor.filters.appliedFilters,
    }))
  }

  /**
   * Closes the editor. A tab it was opened to create goes away unsaved.
   */
  @action.bound
  closeEditor() {
    this.editor?.deactivate()
    this.editor = null
    this._searcher.setSavedFilterPreview(null)
    const draft = this._draft
    this._draft = null
    if (draft) {
      this._searcher.savedFilterList.delete(draft.id)
      if (draft.returnToId) {
        this._selectTab(draft.returnToId)
      }
    }
  }

  /**
   * Copies a tab after itself and opens the copy's editor. A copy of a
   * default starts from it, so it keeps following the default.
   */
  @action.bound
  duplicate(id: string) {
    const tab = this._find(id)
    this.closeMenu()
    if (!tab) {
      return
    }
    this.closeEditor()
    const copy = this._searcher.savedFilterList.addDraft(
      `${tab.name} copy`,
      tab.isDefault ? {} : tab.filters,
      this._baseFor(tab),
      id,
    )
    if (copy) {
      this._editDraft(copy.id, false)
    }
  }

  /**
   * Starts a tab from the selected one plus the filter bar's filters, after
   * the selected one, and opens its editor. Saving it clears the filter bar.
   * On a default, the new tab starts from it.
   */
  @action.bound
  saveAsTab() {
    this.closeEditor()
    const current = this._searcher.savedFilter
    const list = this._searcher.savedFilterList
    const baseId = current?.isDefault
      ? this._baseFor(current)
      : (current?.baseId ?? null)
    const baseKeys = Object.keys(list.defaultOf(baseId)?.filters ?? {})
    // The default already sets these, and the tab can't set them again.
    const chips = Object.fromEntries(
      Object.entries(this._searcher.filters.serializeExplicit()).filter(
        ([key]) => !baseKeys.includes(key),
      ),
    )
    const added = list.addDraft(
      'New tab',
      current?.isDefault ? chips : { ...current?.filters, ...chips },
      baseId,
      current?.id,
    )
    if (added) {
      this._editDraft(added.id, true)
    }
  }

  /**
   * Adds an empty tab of the viewer's own at the end and opens its editor.
   */
  @action.bound
  newTab() {
    this.closeEditor()
    const added = this._searcher.savedFilterList.addDraft('New tab', {}, null)
    if (added) {
      this._editDraft(added.id, false)
    }
  }

  /**
   * Hides a tab, unless it's the last one shown.
   */
  @action.bound
  hide(id: string) {
    this.closeMenu()
    if (!this.canRemove(id)) {
      return
    }
    this._searcher.savedFilterList.setHidden(id, true)
    this._leaveIfSelected(id)
  }

  /**
   * Hides a tab or shows it again.
   */
  @action.bound
  toggleHidden(id: string) {
    if (this._find(id)?.isHidden) {
      this._searcher.savedFilterList.setHidden(id, false)
    } else {
      this.hide(id)
    }
  }

  /**
   * Deletes a tab, unless it's the last one shown. A default goes straight
   * away, since it can be added back; one of the viewer's own asks first.
   */
  @action.bound
  requestDelete(id: string) {
    this.closeMenu()
    const tab = this._find(id)
    if (!tab || !this.canRemove(id)) {
      return
    }
    if (tab.isDefault) {
      this._delete(id)
    } else {
      this.deletingTabId = id
    }
  }

  @action.bound
  cancelDelete() {
    this.deletingTabId = null
  }

  /**
   * Deletes the tab waiting for confirmation.
   */
  @action.bound
  confirmDelete() {
    const id = this.deletingTabId
    this.deletingTabId = null
    if (id) {
      this._delete(id)
    }
  }

  /**
   * Adds a deleted default back, in its old place.
   */
  @action.bound
  addBack(id: string) {
    this._searcher.savedFilterList.addBack(id)
  }

  /**
   * Asks to confirm putting every default back.
   */
  @action.bound
  requestReset() {
    this.isConfirmingReset = true
  }

  @action.bound
  cancelReset() {
    this.isConfirmingReset = false
  }

  /**
   * Puts every default back in its original order, keeping the viewer's own
   * tabs after them.
   */
  @action.bound
  confirmReset() {
    this.isConfirmingReset = false
    this._searcher.savedFilterList.resetDefaults()
  }

  /**
   * Moves a tab in front of another, or to the end.
   */
  @action.bound
  move(id: string, beforeId: string | null) {
    this._searcher.savedFilterList.move(id, beforeId)
  }

  /**
   * Copies a link that opens one of the viewer's own tabs for whoever
   * follows it. Defaults aren't shared, since everyone has them.
   */
  @action.bound
  async copyLink(id: string) {
    const tab = this._find(id)
    this.closeMenu()
    if (!tab || tab.isDefault) {
      return
    }
    const param = encodeSharedJobFilter({
      name: tab.name,
      filters: tab.filters,
      baseId: tab.baseId,
      sharedBy: this._getSharerName(),
    })
    const url = this._getShareUrl(`${sharedJobFilterParam}=${param}`)
    try {
      await this._writeToClipboard(url)
    } catch (err: unknown) {
      this._newAlert()
        .withState('error')
        .saying('Copying the link failed', extractMessageFromError(err))
        .autoDismiss(20_000)
        .show()
      return
    }
    this._newAlert()
      .withState('success')
      .saying('Link copied', 'Whoever opens it can add a copy of this tab.')
      .autoDismiss()
      .show()
  }

  /**
   * Opens the tab shared in the query string. Returns the query string
   * without it, or `null` when nothing was shared.
   */
  @action.bound
  openSharedInQuery(search: string): string | null {
    const qs = new URLSearchParams(search)
    const param = qs.get(sharedJobFilterParam)
    if (param === null) {
      return null
    }
    qs.delete(sharedJobFilterParam)
    const shared = decodeSharedJobFilter(param)
    if (!shared) {
      this._newAlert()
        .withState('error')
        .saying('This link doesn’t open a tab', 'Ask for a new link.')
        .autoDismiss()
        .show()
      return qs.toString()
    }
    this.closeEditor()
    // The page may not have loaded its settings yet, so this selects without
    // saving them. Only filters and defaults this page knows are kept from
    // the link.
    this._searcher.openSharedFilter({
      ...shared,
      filters: this._searcher.filters.fork(shared.filters).serializeExplicit(),
      baseId:
        this._searcher.savedFilterList.defaultOf(shared.baseId)?.id ?? null,
    })
    return qs.toString()
  }

  /**
   * Keeps the shared tab as one of the viewer's own.
   */
  @action.bound
  addSharedTab() {
    const shared = this._searcher.sharedFilter
    if (!shared) {
      return
    }
    const added = this._searcher.savedFilterList.add(
      shared.name,
      shared.filters,
      shared.baseId,
    )
    if (!added) {
      return
    }
    this._searcher.closeSharedFilter()
    this._selectTab(added.id)
  }

  /**
   * Closes the shared tab without keeping it.
   */
  @action.bound
  dismissSharedTab() {
    const wasSelected = this.selectedTabId === sharedSavedFilterId
    this._searcher.closeSharedFilter()
    if (wasSelected) {
      this._selectRememberedTab()
    }
  }

  /**
   * Opens the editor for a tab just created, which goes away unless saved.
   * Any other editor is already closed, so the tab to go back to is a saved one.
   */
  private _editDraft(id: string, clearsFilterBar: boolean) {
    const returnToId = this.selectedTabId
    this.edit(id)
    this._draft = { id, returnToId, clearsFilterBar }
  }

  /**
   * What a tab built from `tab` starts from: the default itself, unless it
   * filters nothing, or the default the tab already starts from.
   */
  private _baseFor(tab: SavedJobFilter) {
    if (!tab.isDefault) {
      return tab.baseId
    }
    return Object.keys(tab.filters).length > 0 ? tab.id : null
  }

  private _delete(id: string) {
    if (this.editor?.savedFilterId === id) {
      this._draft = null
      this.closeEditor()
    }
    this._searcher.savedFilterList.delete(id)
    this._leaveIfSelected(id)
  }

  private _find(id: string | null) {
    return this._searcher.savedFilterList.all.find((t) => t.id === id) ?? null
  }

  private _leaveIfSelected(id: string) {
    if (this.selectedTabId === id) {
      this._selectTab(this._searcher.defaultSavedFilter.id)
    }
  }
}
